import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { AccurateDatabase, AccurateStatus } from '../api/types';
import { useSession } from '../lib/session';
import { UserRole } from '../api/enums';
import DangerZone from '../components/DangerZone';
import { useConfirm } from '../components/confirm';
import { Alert, Button, Card, Field, Modal, Spinner } from '../components/ui';
import { localizeMessage, useLocale, useT } from '../i18n/locale';

interface AvailableDb {
  id: string;
  alias: string;
  expired: boolean;
  trial: boolean;
  connected: boolean;
  databaseId: string | null;
}

export default function AccurateSettingsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { databases, refreshDatabases, user } = useSession();

  const [status, setStatus] = useState<AccurateStatus | null>(null);
  const [available, setAvailable] = useState<AvailableDb[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(searchParams.get('error'));
  const [notice, setNotice] = useState<string | null>(
    searchParams.get('connected') ? t('acc.connectedOk') : null,
  );
  const [picking, setPicking] = useState<AvailableDb | null>(null);
  const [editing, setEditing] = useState<AccurateDatabase | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [edrcField, setEdrcField] = useState('numericField1');
  /*
   * Errors raised while the modal is open must render INSIDE it. Page-level
   * `error` sits behind the overlay, so a failed connect looked like the
   * button simply did nothing.
   */
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalErrorIsPlanLimit, setModalErrorIsPlanLimit] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.get<AccurateStatus>('/accurate/status'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('acc.statusFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
    // Clear the one-shot query flags so a refresh does not replay the banner.
    if (searchParams.has('error') || searchParams.has('connected')) {
      setSearchParams({}, { replace: true });
    }
     
  }, [loadStatus]);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const { authorizeUrl } = await api.post<{ authorizeUrl: string }>('/accurate/connect');
      // Full-page navigation: Accurate's consent screen refuses to be framed.
      window.location.href = authorizeUrl;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('acc.startFailed'));
      setBusy(false);
    }
  }

  async function loadAvailable() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.get<{ databases: AvailableDb[] }>('/accurate/available');
      setAvailable(data.databases);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('acc.listFailed'));
    } finally {
      setBusy(false);
    }
  }

  function openPicker(db: AvailableDb) {
    setPicking(db);
    setEdrcField('numericField1');
    setModalError(null);
    setModalErrorIsPlanLimit(false);
  }

  function closePicker() {
    setPicking(null);
    setModalError(null);
    setModalErrorIsPlanLimit(false);
  }

  async function addDatabase() {
    if (!picking) return;
    setBusy(true);
    setModalError(null);
    try {
      await api.post('/accurate/databases', {
        aolDbId: picking.id,
        alias: picking.alias,
        edrcField,
      });
      const alias = picking.alias;
      closePicker();
      setNotice(t('acc.dbConnected', { alias }));
      await Promise.all([refreshDatabases(), loadAvailable()]);
    } catch (e) {
      // 402 means the plan is the blocker, not the request — offer the upgrade
      // rather than just restating the limit.
      setModalErrorIsPlanLimit(e instanceof ApiError && e.status === 402);
      setModalError(e instanceof ApiError ? e.message : t('acc.connectDbFailed'));
    } finally {
      setBusy(false);
    }
  }

  function openEditor(db: AccurateDatabase) {
    setEditing(db);
    setEdrcField(db.edrcField);
    setModalError(null);
    setModalErrorIsPlanLimit(false);
  }

  async function saveEdit() {
    if (!editing) return;
    setBusy(true);
    setModalError(null);
    try {
      await api.patch(`/accurate/databases/${editing.id}`, { edrcField });
      const alias = editing.alias;
      setEditing(null);
      setNotice(t('acc.edrcUpdated', { alias, field: edrcField }));
      await refreshDatabases();
    } catch (e) {
      setModalError(e instanceof ApiError ? e.message : t('acc.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Revokes the whole Accurate grant. Every database under it is released,
   * so this is worth a blunt confirmation rather than a quiet action.
   */
  async function disconnectAccount() {
    const count = databases.length;
    const ok = await confirm({
      title: t('acc.confirmDisconnectTitle'),
      tone: 'danger',
      confirmLabel: t('acc.confirmDisconnectYes'),
      body:
        count > 0
          ? t('acc.confirmDisconnectBody', { count })
          : t('acc.confirmDisconnectBodyNone'),
      details:
        count > 0
          ? [
              { label: t('acc.confirmDbReleased'), value: String(count) },
              { label: t('acc.confirmHistoryKept'), value: t('acc.confirmHistoryKeptValue') },
            ]
          : undefined,
    });
    if (!ok) return;

    setDisconnecting(true);
    setError(null);
    try {
      const res = await api.post<{
        message: string;
        messageCode?: string;
        disconnectedDatabases: number;
      }>('/accurate/disconnect');
      setNotice(
        localizeMessage(locale, res.messageCode, res.message, {
          count: res.disconnectedDatabases,
        }),
      );
      setAvailable(null);
      await Promise.all([refreshDatabases(), loadStatus()]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('acc.disconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  }

  async function removeDatabase(id: string, alias: string) {
    const ok = await confirm({
      title: t('acc.confirmRemoveTitle', { alias }),
      tone: 'warn',
      confirmLabel: t('acc.disconnectShort'),
      body: t('acc.confirmRemoveBody'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/accurate/databases/${id}`);
      await Promise.all([refreshDatabases(), loadAvailable()]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('acc.removeDbFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label={t('common.loading')} />;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
      <h2 className="m-0 text-lg font-bold text-white">{t('acc.title')}</h2>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <Card className="p-5">
        {status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <span className="font-bold text-slate-800">{t('acc.connected')}</span>
            </div>
            <dl className="grid gap-2 text-sm md:grid-cols-2">
              <Detail label={t('acc.account')} value={status.accurateUser ?? '-'} />
              <Detail
                label={t('acc.validUntil')}
                value={
                  status.accessExpiresAt
                    ? new Date(status.accessExpiresAt).toLocaleString('id-ID')
                    : '-'
                }
              />
              <Detail
                label={t('acc.lastRefreshed')}
                value={
                  status.lastRefreshedAt
                    ? new Date(status.lastRefreshedAt).toLocaleString('id-ID')
                    : t('acc.never')
                }
              />
            </dl>
            <p className="m-0 text-xs text-slate-500">
              {t('acc.autoRefresh')}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="secondary" onClick={() => void connect()} disabled={busy}>
                Hubungkan Ulang
              </Button>
              <Button variant="secondary" onClick={() => void loadAvailable()} disabled={busy}>
                Lihat Database Tersedia
              </Button>
              <div className="flex-1" />
              <Button
                variant="danger"
                onClick={() => void disconnectAccount()}
                disabled={busy || disconnecting}
              >
                {disconnecting ? t('acc.disconnecting') : t('acc.disconnect')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="font-bold text-slate-800">{t('acc.notConnected')}</span>
            </div>
            {status?.lastError ? <Alert tone="warn">{status.lastError}</Alert> : null}
            <p className="m-0 text-sm text-slate-600">
              {t('acc.connectHelp')}
            </p>
            <Button onClick={() => void connect()} disabled={busy}>
              {busy ? t('acc.redirecting') : t('acc.connectAccount')}
            </Button>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="m-0 mb-3 text-base font-bold text-slate-800">{t('acc.connectedDatabases')}</h3>
        {databases.length === 0 ? (
          <p className="m-0 text-sm text-slate-500">{t('acc.noDatabases')}</p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {databases.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-800">{d.alias}</div>
                  <div className="text-[11px] text-slate-500">
                    {t('acc.accurateId', { id: d.aolDbId })} · ED RC:{' '}
                    <span className="font-semibold text-slate-700">{d.edrcField}</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => openEditor(d)}
                    disabled={busy}
                    className="cursor-pointer rounded-lg border-0 bg-transparent px-3 py-1.5 text-xs font-bold text-brand-500 hover:bg-brand-50 disabled:opacity-50"
                  >
                    {t('acc.editEdrc')}
                  </button>
                  <button
                    onClick={() => void removeDatabase(d.id, d.alias)}
                    disabled={busy}
                    className="cursor-pointer rounded-lg border-0 bg-transparent px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 disabled:opacity-50"
                  >
                    {busy ? t('acc.processing') : t('acc.disconnectShort')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {available ? (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <h4 className="m-0 mb-2 text-sm font-bold text-slate-700">{t('acc.availableTitle')}</h4>
            <ul className="m-0 list-none space-y-2 p-0">
              {available.map((db) => (
                <li
                  key={db.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-bold text-slate-800">{db.alias}</div>
                    <div className="text-[11px] text-slate-500">
                      ID {db.id}
                      {db.trial ? ' · trial' : ''}
                      {db.expired ? ' · kedaluwarsa' : ''}
                    </div>
                  </div>
                  {db.connected ? (
                    <span className="text-xs font-bold text-emerald-600">{t('acc.alreadyConnected')}</span>
                  ) : (
                    <Button
                      onClick={() => openPicker(db)}
                      disabled={db.expired || busy}
                      className="px-4 py-1.5 text-xs"
                    >
                      {t('acc.connect')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>

      <Modal
        open={picking !== null}
        title={t('acc.connectTitle', { alias: picking?.alias ?? '' })}
        onClose={closePicker}
        footer={
          <>
            <Button variant="secondary" onClick={closePicker}>
              {modalError ? t('common.close') : t('common.cancel')}
            </Button>
            {modalErrorIsPlanLimit ? (
              <Button onClick={() => navigate('/billing')}>{t('acc.viewPlans')}</Button>
            ) : (
              <Button onClick={() => void addDatabase()} disabled={busy}>
                {busy ? t('acc.connecting') : t('acc.connect')}
              </Button>
            )}
          </>
        }
      >
        {modalError ? (
          <div className="mb-4">
            <Alert tone={modalErrorIsPlanLimit ? 'warn' : 'error'}>
              {modalError}
              {modalErrorIsPlanLimit ? (
                <div className="mt-2">
                  <Link to="/billing" className="font-bold underline">
                    {t('acc.upgradeNow')}
                  </Link>
                </div>
              ) : null}
            </Alert>
          </div>
        ) : null}

        <Alert tone="info">
          {t('acc.edrcHelp')}
        </Alert>
        <div className="mt-4">
          <Field label={t('acc.edrcField')}>
            <select
              value={edrcField}
              onChange={(e) => setEdrcField(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800"
            >
              {Array.from({ length: 10 }, (_, i) => `numericField${i + 1}`).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Modal>
      {user?.role === UserRole.Owner ? <DangerZone /> : null}

      <Modal
        open={editing !== null}
        title={t('acc.edrcEditTitle', { alias: editing?.alias ?? '' })}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>
              Batal
            </Button>
            <Button onClick={() => void saveEdit()} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        {modalError ? (
          <div className="mb-4">
            <Alert tone="error">{modalError}</Alert>
          </div>
        ) : null}

        <Alert tone="info">
          {t('acc.edrcEditHelp')}
        </Alert>

        <div className="mt-4">
          <Field label={t('acc.edrcField')} hint={t('acc.edrcCurrent', { value: editing?.edrcField ?? '-' })}>
            <select
              value={edrcField}
              onChange={(e) => setEdrcField(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800"
            >
              {Array.from({ length: 10 }, (_, i) => `numericField${i + 1}`).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Modal>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="m-0 text-[11px] font-bold tracking-wide text-slate-500 uppercase">{label}</dt>
      <dd className="m-0 truncate text-sm font-semibold text-slate-800" title={value}>
        {value}
      </dd>
    </div>
  );
}
